import { Table } from "antd";
import type { Key } from "antd/es/table/interface";
import type { ColumnsType, TableProps } from "antd/lib/table";
import i18n from "i18next";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { getAssignedDataGroup } from "../../../../core/router/auth";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { Entity } from "../../../../core/types/fe";
import { ActionBar } from "../../../ActionBar/ActionBar";
import { SecondaryButton } from "../../../Buttons/components/ActionButton";
import { CopyButton } from "../../../FeatureButtons/CopyButton";
import { DeleteButton } from "../../../FeatureButtons/DeleteButton";
import { NotificationType, showSuccessNotification } from "../../../NotificationAlerts";
import { NotificationAlert } from "../../../NotificationAlerts/NotificationAlert";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../NotificationAlerts/types";
import { type ExtendedColumnsType, useUpdateTableDataMutation } from "../../index";
import { TableStyle } from "../../styles/AntdTableStyle";
import { Actions } from "../../types/types";

export interface PropertyTableProps<T> {
    getColumnsConfig: (t: Function) => ExtendedColumnsType<T>[];
    propertyAsRowKey: keyof T;
    tableData: T[];
    readOnly: boolean;
    onDataChanged?: Function;
    elementSelector?: React.JSX.Element;
    actions?: Actions[];
    actionCallbacks?: {
        add?: () => void;
        delete?: (item?: Entity) => void;
        copy?: (item: Entity) => void;
        formCopy?: () => void;
    };
    customActions?: React.JSX.Element[];
    onRow?: TableProps<T>["onRow"];
    rowsAreSelectable?: boolean;
    isFetching?: boolean;
    name?: string;
    readOnlyVisibilityEnabled?: boolean;
    resourceUrl?: string;
    pagination?: TableProps<T>["pagination"] | false;
    addButtonName?: string;
    onSelectionChange?: (selectedRowKeys: Key[]) => void;
}

export type DataItemWithKey<T> = T & { key: T[keyof T] };

export const PropertyTable = <T extends Entity>({
    getColumnsConfig,
    propertyAsRowKey,
    tableData = [],
    readOnly,
    elementSelector,
    actions,
    actionCallbacks,
    onDataChanged,
    customActions,
    onRow,
    rowsAreSelectable = true,
    isFetching,
    name,
    readOnlyVisibilityEnabled,
    resourceUrl,
    pagination,
    addButtonName = "addButton",
    onSelectionChange,
}: PropertyTableProps<T>) => {
    const { t } = useTranslation();

    const [selectedElement, setSelectedElement] = useState<T | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    const [hasDuplicates, setHasDuplicates] = useState(false);

    const baseURI = `data-groups/${getAssignedDataGroup()?.code}`;
    const resourceId = selectedRows?.length ? selectedRows[0].id : "";

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const addRow = () => {
        if (selectedElement === null) return;

        if (onDataChanged) {
            const exists = tableData.some((x) => x[propertyAsRowKey] === selectedElement[propertyAsRowKey]);
            if (!exists) {
                const newTableData = [...tableData, selectedElement];
                onDataChanged(newTableData);
                setSelectedElement(null);
                setHasDuplicates(false);
            } else {
                setHasDuplicates(true);
            }
        }
    };

    const deleteRow = () => {
        if (onDataChanged) {
            const newValue = tableData.filter((element) => {
                const key = element[propertyAsRowKey as keyof Entity];
                return typeof key !== "boolean" && key !== undefined && !selectedRowKeys.includes(key);
            });
            setSelectedRowKeys([]);
            onDataChanged(newValue);
        }
    };

    const defaultColumns = useMemo(() => getColumnsConfig(t).filter((item) => !item.hidden), [t, getColumnsConfig]);

    const columns = defaultColumns.map((col) => {
        return col;
    });

    const onSelectChange = (newSelectedRowKeys: Key[], rows: T[]) => {
        setSelectedRows(rows);
        setSelectedRowKeys(newSelectedRowKeys);
        onSelectionChange?.(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const canAdd = actions?.some((action) => action === Actions.ADD);
    const canDelete = actions?.some((action) => action === Actions.DELETE);
    const canCopy = actions?.some((action) => action === Actions.COPY);
    const canFormCopy = actions?.some((action) => action === Actions.FORM_COPY);

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const [updateTable, { isLoading: isUpdating }] = useUpdateTableDataMutation();

    const backendCopy = useCallback(() => {
        updateTable({
            url: `${baseURI}/${resourceUrl}/${resourceId}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            setSelectedRows([]);
            const payloadIdObject = payload as Entity;
            navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${payloadIdObject.id}`);
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    }, [updateTable, baseURI, resourceUrl, resourceId, navigate, notifyOkCopy]);

    const formCopy = useCallback(() => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.COPY}/${resourceId}`);
    }, [navigate, resourceId, resourceUrl]);

    return (
        <>
            {elementSelector &&
                React.cloneElement(elementSelector, {
                    onSelected: setSelectedElement,
                })}
            <ActionBar className="left">
                {canAdd && (
                    <SecondaryButton
                        onClick={actionCallbacks?.add || addRow}
                        disabled={elementSelector !== undefined && !selectedElement}
                        name={addButtonName}
                    >
                        {i18n.t("button.add")}
                    </SecondaryButton>
                )}
                {canDelete && (
                    <DeleteButton
                        callback={actionCallbacks?.delete || deleteRow}
                        isDisabled={selectedRowKeys.length !== 1}
                        itemToDelete={selectedRows[0]}
                    />
                )}
                {canCopy && (
                    <CopyButton
                        isDisabled={selectedRowKeys.length !== 1}
                        itemToCopy={selectedRows[0]}
                        callback={actionCallbacks?.copy || backendCopy}
                    />
                )}
                {canFormCopy && (
                    <CopyButton
                        isDisabled={selectedRowKeys.length !== 1}
                        itemToCopy={selectedRows[0]}
                        callback={actionCallbacks?.formCopy || formCopy}
                    />
                )}
                {customActions?.map((action) => action)}
            </ActionBar>

            {hasDuplicates && (
                <NotificationAlert description="common.input.duplicatedEntry.error" type={NotificationType.ERROR} isCloseable={false} />
            )}
            <TableStyle>
                <Table<T>
                    id={name}
                    loading={isFetching || isUpdating}
                    rowSelection={rowsAreSelectable ? rowSelection : undefined}
                    columns={columns as ColumnsType<T>}
                    dataSource={addKeyProp<T>(tableData as Readonly<T[]>, propertyAsRowKey)}
                    onRow={onRow}
                    pagination={pagination ?? { placement: ["bottomCenter"], showSizeChanger: true }}
                    rowClassName={(record) => {
                        if (readOnlyVisibilityEnabled) {
                            return readOnly || record.readOnly ? "aevi-read-only-row" : "";
                        }
                        return "";
                    }}
                />
            </TableStyle>
        </>
    );
};

// antd table requires to have a key prop in the table's row object, otherwise
// it will log warning in the console
function addKeyProp<T>(array: ReadonlyArray<T>, propertyAsRowKey: keyof T) {
    return array?.map((obj) => {
        return { ...obj, key: obj[propertyAsRowKey] } as DataItemWithKey<T>;
    });
}
